import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";

import formatIndexNumber from "common/format-index-number";

import FaqTableProps, { FaqUpdateDto, FaqDataDto } from "dto/components/pages/faq/page-faq-table-dto";

import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";

import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";
import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";

const FaqTable: React.FC<FaqTableProps> = ({ faqs, setDisableAddNew, newRow, setNewRow, onUpdateFaqs, onCreateFaqs, onHandlePageChange, fetching, paginationTable }) => {
	const tableRef = useRef<HTMLDivElement | null>(null);
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [faqUpdatedData, setFaqUpdatedData] = useState<FaqUpdateDto>();
	const [faqData, setFaqData] = useState<FaqDataDto>(faqs);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const isEmptyData = useMemo(() => faqData.length === 0 && !fetching, [faqData.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
		setEditingRow(id);
		setDisableAddNew(true);
	}, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "question" | "answer", value: string) => {
		const updatedFaq = faqUpdatedData ?? faqData.find((faq) => faq.id === id);

		if (updatedFaq) {
			const updatedData = { ...updatedFaq, [field]: value, status: "active" };

			setFaqUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
	}, [faqData, faqUpdatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["question", "answer"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (faqUpdatedData) {
			const rowErrors = validateRow(faqUpdatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newFaq = faqData.map((faq) => (faq.id === id ? { ...faq, ...faqUpdatedData } : faq));

			setFaqData(newFaq);
			onUpdateFaqs(faqUpdatedData);
			setIsReadyData(false);
			setEditingRow(null);
			setFaqUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [faqData, faqUpdatedData, onUpdateFaqs, setDisableAddNew]);

	//prettier-ignore
	const onHandleMove = useCallback((id: string, move: number) => {
		const index = faqData.findIndex((faq) => faq.id === id);
		const updatedFaq = faqData[index];

		if ((index > 0 && move === -1) || (index < faqData.length - 1 && move === 1)) {
			const newFaqs = [...faqData];
			const [movedFaq] = newFaqs.splice(index, 1);
			const movedDataSortNumber = faqData[index + move].sortNumber;
			const updatedData = { ...updatedFaq, sortNumber: movedDataSortNumber };

			if (updatedData) setFaqUpdatedData(updatedData);

			newFaqs.splice(index + move, 0, movedFaq);

			setFaqData(newFaqs);
		}
	}, [faqData]);

	//prettier-ignore
	const onHandleDelete = useCallback((id: string) => {
		const updatedFaq = faqData.find((faq) => faq.id === id);

		if (updatedFaq) {
			const updatedData = { ...updatedFaq, status: "inactive" };
			const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;
			
			onUpdateFaqs(updatedData, changePage);
		}

		setEditingRow(null);
		setFaqUpdatedData(null);
		setDisableAddNew(false);
	}, [faqData, setDisableAddNew, paginationTable, onUpdateFaqs]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setNewRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newFaq = {
			question: newRow?.question || "",
			answer: newRow?.answer || "",
			status: "active",
		};

		onCreateFaqs(newFaq);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const cancelEditing = useCallback(() => {
		setEditingRow(null);
		setFaqUpdatedData(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, [editingRow || ""]: {} }));
	}, [setDisableAddNew, editingRow]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (editingRow && tableRef.current && !tableRef.current.contains(event.target as Node)) cancelEditing();
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [editingRow, cancelEditing]);

	useEffect(() => {
		if (!fetching) setIsReadyData(true);
	}, [fetching, faqs]);

	useEffect(() => {
		if (!editingRow && isReadyData) setFaqData(faqs);
	}, [editingRow, faqs, isReadyData]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-faq-table">
			<div className="faq-table">
				<div className="faq-table__wrapper" ref={tableRef}>
					<table className="faq-table__container">
						<thead className="faq-table__header">
							<tr>
								<th className="faq-table__th">No.</th>

								<th className="faq-table__th">Question</th>

								<th className="faq-table__th">Answer</th>
							</tr>
						</thead>

						<tbody className="faq-table__content">
							{faqData.map((faq, index) => {
								const isEditing = editingRow === faq.id;
								const isExpanded = expandedRows.includes(faq.id);
								const rowErrors = errors[faq.id] || {};

								return (
									<tr className="faq-table__tr" key={faq.id}>
										<td className="faq-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="faq-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="question" value={faqUpdatedData?.question ?? faq.question} error={rowErrors.question} touched={!!faqUpdatedData} maxCharLength={90} maxRows={20} onChange={(e) => onHandleInputChange(faq.id, "question", e.target.value)} />
											) : isExpanded ? (
												faq.question
											) : (
												truncateText(faq.question, 60)
											)}
										</td>

										<td className="faq-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="answer" value={faqUpdatedData?.answer ?? faq.answer} touched={!!faqUpdatedData} error={rowErrors.answer} maxCharLength={500} maxRows={60} onChange={(e) => onHandleInputChange(faq.id, "answer", e.target.value)} />
											) : isExpanded ? (
												faq.answer
											) : (
												truncateText(faq.answer, 100)
											)}
										</td>

										<td className="faq-table__td">
											{isEditing ? (
												<div className="faq-table__edit">
													<button className="faq-table__icon-button--bordered" onClick={() => onHandleDelete(faq.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button>

													<button className="faq-table__icon-button--bordered" onClick={() => onHandleMove(faq.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="faq-table__icon-button--bordered" onClick={() => onHandleMove(faq.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button>

													<button className="faq-table__icon-button--save" onClick={() => onHandleSave(faq.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="faq-table__actions">
													<button className="faq-table__icon-button" onClick={() => toggleRow(faq.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="faq-table__icon-button" onClick={() => onHandleEdit(faq.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="faq-table__tr" ref={newRowRef}>
									<td className="faq-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="faq-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-question" value={newRow?.question || ""} error={errors["new"]?.question} touched={!!newRow} maxCharLength={90} maxRows={20} placeholder="Enter question here" onChange={(e) => onHandleNewInputChange("question", e.target.value)} />
									</td>

									<td className="faq-table__td">
										{/* prettier-ignore */}
										<AppTextarea fieldRequired name="new-answer" value={newRow?.answer || ""} error={errors["new"]?.answer} touched={!!newRow} maxCharLength={500} maxRows={60} placeholder="Enter answer here" onChange={(e) => onHandleNewInputChange("answer", e.target.value)} />
									</td>

									<td className="faq-table__td">
										<div className="faq-table__action-container">
											<button className="faq-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="faq-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="faq-table__tr">
									<td className="faq-table__message" colSpan={4}>
										Loading...
									</td>
								</tr>
							)}

							{isEmptyData && (
								<tr className="faq-table__tr">
									<td className="faq-table__message" colSpan={4}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default FaqTable;
